import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { IEvent } from '../../store/events/interfaces';

import {
  Badge,
  Col,
  Overlay,
  OverlayTrigger,
  ProgressBar,
  Row,
  Tooltip,
} from 'react-bootstrap';

export type GenderOccupationResult = {
  label: string
  percent: number
  isFullOccupation: boolean
  isUnlimited: boolean
  genderKey: string
}

export const EventOccupation = (props: { event: IEvent; isModal?: boolean }) => {
  const { event, isModal } = props;
  const { data: program } = useSelector((state: RootState) => state.program);
  const targets = useRef<Array<HTMLDivElement | null>>([]);
  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);
  const [show3, setShow3] = useState(false);
  const shows = [show1, show2, show3];
  const setShows = [setShow1, setShow2, setShow3];
  const occupationResults: [string, number, boolean, boolean, string, string, string][] = [];

  const genderInfo = event.registration_statistics.gender_info_combined;
  const noParticipantsAllowed = 'none' in genderInfo;

  const genderSymbols: Record<string, string> = {
    w: '♀',
    m: '♂',
    d: '⚲',
    mw: '♂+♀',
    md: '♂+⚲',
    wd: '♀+⚲',
    mwd: '',
  };

  for (const [key, value] of Object.entries(genderInfo)) {
    let currentOccupation = value[0];
    const maxOccupation = value[1];
    const color = value[2];
    const occupationText = value[3];
    const isUnlimited = maxOccupation === null || maxOccupation === undefined;
    const isFullOccupation = currentOccupation >= maxOccupation;
    let occupationPercentage;

    if (isUnlimited) {
      occupationPercentage = 0;
    } else if (isFullOccupation) {
      occupationPercentage = 100;
    } else {
      const totalSlots = maxOccupation + event.registration_statistics.waiting_list_occupied;
      occupationPercentage = (currentOccupation / totalSlots) * 100;
    }

    if (noParticipantsAllowed) {
      occupationResults.push([
        'Keine Teilnahme erlaubt',
        100,
        true,
        false,
        'none',
        color,
        occupationText
      ]);
    } else {
      occupationResults.push([
        program.booking_visibility === 'detailed'
          ? `${genderSymbols[key] ? `${genderSymbols[key]}  - `: ''}${
              isUnlimited
                ? `${currentOccupation}/Unbegrenzt`
                : `${Math.min(currentOccupation, maxOccupation)}/${maxOccupation !== null ? maxOccupation : '∞'}`
            }`
          : genderSymbols[key],
        occupationPercentage,
        isFullOccupation || (currentOccupation >= maxOccupation),
        isUnlimited,
        key,
        color,
        occupationText
      ]);
    }
  }

  let totalCodeSlots = 0;

  if (event.get_remaining_soft_quota_slots) {
    const codeSlots = (event.get_remaining_soft_quota_slots as unknown) as {
      [key: string]: number;
    };

    totalCodeSlots = Object.values(codeSlots).reduce(
      (a: number, b: number) => a + b,
      0
    );
  }
  if (event.has_external_registration) {
    return null;
  }

  return (
    <>
      {occupationResults.map((result, key) => {
        const [label, percent, isFullOccupation, isUnlimited, genderKey, color, occupationText] = result;
        const progressBarVariant = color;

        const progressBarNow = isUnlimited ? 0 : isFullOccupation ? 100 : percent;

        return (
          <Row key={key}>
            <Col
              md={12}
              className="d-flex justify-content-center align-items-start"
            >
              <Badge className='genderBadge'>{label}</Badge>
              <div className="sr-only" tabIndex={0}>
                Aktuelle Belegung für die Veranstaltung "{event.name}".
                &nbsp;{occupationText}
              </div>
            </Col>
            <Col md={{ span: 10, offset: 1 }}>
              <div
                ref={(el) => (targets.current[key] = el)}
                onClick={() => setShows[key](!shows[key])}
                tabIndex={-1}
                onBlur={() => (shows[key] ? setShows[key](!shows[key]) : null)}
              >
                <ProgressBar
                  now={progressBarNow}
                  variant={progressBarVariant}
                  className="mb-2"
                  aria-label="Belegung nach Geschlecht"
                />
                {program.booking_visibility_codes !== 'never' &&
                  !isModal &&
                  totalCodeSlots > 0 && (
                    <OverlayTrigger
                      overlay={
                        <Tooltip id="tooltip-disabled">
                          Weitere Informationen zu den Code Belegungen sind in der Detailansicht dieser Veranstaltung zu finden.
                        </Tooltip>
                      }
                    >
                      <span className="small text-muted">
                        {totalCodeSlots} verfügbare Codekategorie Plätze
                      </span>
                    </OverlayTrigger>
                  )}
              </div>
              {targets.current[key] && (
                <Overlay
                  target={targets.current[key]}
                  show={shows[key]}
                  placement="bottom"
                >
                  {(props: any) => (
                    <Tooltip {...props}>
                      Aktuelle Belegung für die Veranstaltung "{event.name}".
                      &nbsp;{occupationText}
                    </Tooltip>
                  )}
                </Overlay>
              )}
            </Col>
          </Row>
        )
      })}
    </>
  );
};
